import { useEffect, useState } from "react";
import Heading from "@/components/tpr/Heading/Heading";
import UploadAvatarDialog from "@/components/tpr/UploadAvatarDialog/UploadAvatarDialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BreadcrumbItem, BreadcrumbLink } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Item, ItemActions, ItemContent, ItemDescription, ItemTitle } from "@/components/ui/item";
import { defaultItemClasses, secondaryButtonClasses } from "@/constants/ui";
import { SHOW_MEMBER_TYPE } from "@/constants/featureFlags";
import { useBreadcrumb } from "@/contexts/breadcrumbContext";
import { useMember } from "@/contexts/memberContext";
import { getInitials } from "@/lib/utils";
import { memberTypeForDisplay } from "@/types/Member";
import { Camera } from "lucide-react";
import { useNavigate } from "react-router";

const AccountView = () => {
  const { member, refreshMember } = useMember();
  const navigate = useNavigate();
  const [isUploadAvatarOpen, setIsUploadAvatarOpen] = useState(false);

  const { setBreadcrumb } = useBreadcrumb();

  useEffect(() => {
    setBreadcrumb(
      <>
        <BreadcrumbItem className="text-sm text-gray-600 truncate max-w-[125px] md:max-w-none">
          <BreadcrumbLink href="#">Account</BreadcrumbLink>
        </BreadcrumbItem>
      </>,
    );
    return () => setBreadcrumb(null);
  }, [setBreadcrumb]);

  const fullName = member ? `${member.firstName} ${member.lastName}` : "User";
  const initials = getInitials(fullName);

  return (
    <div>
      <div className="px-4 min-h-[80vh]">
        <div className="mt-4 mx-auto flex items-center justify-center">
          <div className="relative inline-block">
            <Avatar className="w-32 h-32">
              <AvatarImage
                src={
                  member?.avatar ||
                  "https://images.unsplash.com/photo-1668580112791-2a453788ed9e?ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&q=80&w=256"
                }
                alt={fullName || "User Avatar"}
              />
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            {member && (
              <button
                type="button"
                onClick={() => setIsUploadAvatarOpen(true)}
                className="absolute bottom-0 right-0 w-9 h-9 flex items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md hover:bg-primary/90 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                title="Change avatar"
                aria-label="Change avatar"
                data-testid="account-avatar-upload-trigger"
              >
                <Camera className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        <div className="mb-8 mt-8">
          <Heading title={`${member?.firstName}'s Account`} />
        </div>
        <div className="flex flex-col gap-2">
          <Item variant="outline" className={defaultItemClasses}>
            <ItemContent>
              <div className="flex flex-col">
                <ItemTitle className="block text-base md:text-1xl font-semibold">Email</ItemTitle>
                <ItemDescription>
                  Your email address, used for logging in and communication. Please contact your PM or Head Office if
                  you wish to change this.
                </ItemDescription>
              </div>
            </ItemContent>
            <ItemActions>{member?.email}</ItemActions>
          </Item>
          <Item variant="outline" className={defaultItemClasses}>
            <ItemContent>
              <div className="flex flex-col">
                <ItemTitle className="block text-base md:text-1xl font-semibold">Password</ItemTitle>
                <ItemDescription>
                  Your password is used to secure your account. You can change it at any time here.
                </ItemDescription>
              </div>
            </ItemContent>
            <ItemActions>
              <Button
                variant="outline"
                size="sm"
                className={secondaryButtonClasses}
                data-testid="change-password-button"
              >
                Change Password
              </Button>
            </ItemActions>
          </Item>
          {/* Therapist type hidden for now behind SHOW_MEMBER_TYPE. The type is still stored on the
              member; flip the flag to surface this row again. */}
          {SHOW_MEMBER_TYPE && (
            <Item variant="outline" className={defaultItemClasses}>
              <ItemContent>
                <div className="flex flex-col">
                  <ItemTitle className="block text-base md:text-1xl font-semibold">Therapist Type</ItemTitle>
                  <ItemDescription>
                    We tailor our system to suit the type of therapist you are. Please contact support if you wish to
                    change this.
                  </ItemDescription>
                </div>
              </ItemContent>
              <ItemActions data-testid="account-therapist-type">{memberTypeForDisplay(member?.type)}</ItemActions>
            </Item>
          )}
          <Button className="w-full self-end mt-4 lg:w-auto" variant="default">
            Save Changes
          </Button>

          <div className="mt-8 mb-2">
            <Heading title="Policies" />
          </div>

          <Item variant="outline" className={defaultItemClasses} data-testid="policy-acceptable-use">
            <ItemContent>
              <div className="flex flex-col">
                <ItemTitle className="block text-base md:text-1xl font-semibold">Acceptable Use Policy</ItemTitle>
                <ItemDescription>How we expect members to use our site.</ItemDescription>
              </div>
            </ItemContent>
            <ItemActions>
              <Button
                variant="outline"
                size="sm"
                className={secondaryButtonClasses}
                onClick={() => navigate("/terms/acceptable-use-policy")}
              >
                View
              </Button>
            </ItemActions>
          </Item>

          <Item variant="outline" className={defaultItemClasses} data-testid="policy-privacy">
            <ItemContent>
              <div className="flex flex-col">
                <ItemTitle className="block text-base md:text-1xl font-semibold">Privacy Policy</ItemTitle>
                <ItemDescription>How we look after your personal information.</ItemDescription>
              </div>
            </ItemContent>
            <ItemActions>
              <Button
                variant="outline"
                size="sm"
                className={secondaryButtonClasses}
                onClick={() => navigate("/terms/privacy-policy")}
              >
                View
              </Button>
            </ItemActions>
          </Item>

          <Item variant="outline" className={`${defaultItemClasses} mb-8`} data-testid="policy-cookies">
            <ItemContent>
              <div className="flex flex-col">
                <ItemTitle className="block text-base md:text-1xl font-semibold">
                  Cookies &amp; Similar Technologies Policy
                </ItemTitle>
                <ItemDescription>What we store on your device and why.</ItemDescription>
              </div>
            </ItemContent>
            <ItemActions>
              <Button
                variant="outline"
                size="sm"
                className={secondaryButtonClasses}
                onClick={() => navigate("/terms/cookies-policy")}
              >
                View
              </Button>
            </ItemActions>
          </Item>
        </div>
      </div>
      {member && (
        <UploadAvatarDialog
          member={member}
          open={isUploadAvatarOpen}
          onOpenChange={setIsUploadAvatarOpen}
          onUploadSuccess={() => {
            void refreshMember();
          }}
        />
      )}
    </div>
  );
};

export default AccountView;
